// Cross-host accessibility props.
//
// react-native-web does NOT forward `accessibilityState` — it is absent from
// its `forwardedProps` list, and the only thing in the library that reads a
// state is `AccessibilityUtil/isDisabled.js`, which looks at
// `accessibilityStateS` (plural, an older API) and is imported only by the
// legacy `Touchable`, never by `Pressable`. So on web that prop is inert, not
// degraded. `aria-*` IS forwarded, and RN maps `aria-*` natively too
// (`View.js`: `busy: ariaBusy ?? accessibilityState?.busy`), which makes it the
// one prop that reaches both hosts.
//
// 📖 Full census of what RNW keeps and drops: `next/rnw-strangler.md §4.1`.

/**
 * `aria-busy` for a control whose content is swapped for a spinner while work
 * is in flight (RW-38).
 *
 * ⛔ Emitted ONLY when true. `aria-busy="false"` is the spec default, so
 * spelling it out on every control in the app would be noise on a state that is
 * the default everywhere.
 *
 * ⚠️ This carries the MACHINE-READABLE half only. No mainstream screen reader
 * spontaneously announces `aria-busy` on a `role="button"` — a user who moved
 * focus away after tapping hears nothing. The perceivable half is WCAG 4.1.3
 * Status Messages and needs a live region (`accessibilityLiveRegion`, as
 * `EditorTopBar.tsx` does). That is still open: RW-38.
 */
export const busyAria = (loading: boolean | undefined): { "aria-busy"?: true } =>
  loading ? { "aria-busy": true } : {};

/**
 * `aria-checked` / `aria-disabled` for a checkbox or switch row. react-native-web
 * drops `accessibilityState` (see above), so the state a screen reader announces
 * on web comes from these; RN maps `aria-*` natively too.
 */
export const checkedAria = (
  checked: boolean,
  disabled: boolean | undefined,
): { "aria-checked": boolean; "aria-disabled"?: true } => ({
  "aria-checked": checked,
  ...(disabled ? { "aria-disabled": true as const } : {}),
});

/**
 * `aria-disabled` for a control that looks and answers as "not now" but stays an
 * enabled button so a tap can explain why (IconButton `locked`). Without it the
 * lock is a colour on web, because `accessibilityState` is dropped there. Only
 * when true, like `busyAria`.
 */
export const lockedAria = (locked: boolean | undefined): { "aria-disabled"?: true } =>
  locked ? { "aria-disabled": true } : {};

/** The `accessibilityState` keys RNW forwards to the DOM only as `aria-*`. */
export interface A11yStateAria {
  selected?: boolean;
  expanded?: boolean;
  checked?: boolean;
  disabled?: boolean;
  busy?: boolean;
}

export type StateAriaProps = {
  "aria-selected"?: boolean;
  "aria-pressed"?: boolean;
  "aria-expanded"?: boolean;
  "aria-checked"?: boolean;
  "aria-disabled"?: true;
  "aria-busy"?: true;
};

/** `aria-selected` for a tab, option or toggle. Emitted for `false` too: an
 *  unselected option is information, not the default. */
export const selectedAria = (selected: boolean | undefined): Pick<StateAriaProps, "aria-selected"> =>
  selected === undefined ? {} : { "aria-selected": selected };

/** `aria-expanded` for a disclosure trigger. `false` is emitted (collapsed). */
export const expandedAria = (expanded: boolean | undefined): Pick<StateAriaProps, "aria-expanded"> =>
  expanded === undefined ? {} : { "aria-expanded": expanded };

// `aria-selected` is not valid on every role: a `<button>` has `aria-pressed`,
// a radio/checkbox/menu item has `aria-checked`. Native keeps `selected`.
const selectedKey = (role: string | undefined): "aria-selected" | "aria-pressed" | "aria-checked" => {
  if (role === "button" || role === "link") return "aria-pressed";
  if (role === "radio" || role === "checkbox" || role === "switch" || role === "menuitem") {
    return "aria-checked";
  }
  return "aria-selected";
};

/**
 * One spread that mirrors an `accessibilityState` object into `aria-*`, so a
 * call site keeps its native prop and adds `{...stateAria(sameObject, role)}`:
 * `selected`/`expanded`/`checked` follow `false` too; `disabled`/`busy` only
 * when true (like `lockedAria`/`busyAria`). `role` picks the valid attribute
 * for `selected` (see `selectedKey`). Native RN reads `aria-*` first and falls
 * back to `accessibilityState`, so the native result is unchanged.
 */
export const stateAria = (state: A11yStateAria | undefined, role?: string): StateAriaProps => {
  if (!state) return {};
  const key = selectedKey(role);
  return {
    ...(state.selected === undefined ? {} : { [key]: state.selected }),
    ...expandedAria(state.expanded),
    ...(state.checked === undefined ? {} : { "aria-checked": state.checked }),
    ...(state.disabled ? { "aria-disabled": true as const } : {}),
    ...busyAria(state.busy),
  };
};
